import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import warehouseService from '../services/warehouseService';
import useAuth from '../hooks/useAuth';

const WarehouseContext = createContext();

/**
 * WarehouseContext - Provides global warehouse filtering state
 * - Manages selected warehouse IDs for filtering across modules
 * - Fetches and provides the list of all warehouses
 * - Persists selection in sessionStorage until the session ends
 */
export const WarehouseProvider = ({ children }) => {
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState(() => {
        try {
            const saved = sessionStorage.getItem('selected_warehouse_ids');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [loading, setLoading] = useState(false);
    const { isLoggedIn } = useAuth();

    // Fetch all warehouses when logged in or on startup
    useEffect(() => {
        const fetchWarehouses = async () => {
            if (!isLoggedIn) {
                setWarehouses([]);
                return;
            }

            setLoading(true);
            try {
                // Using force: true to ensure we get the latest list on startup
                const response = await warehouseService.getAllWarehouses({}, { force: true });
                if (response.success && Array.isArray(response.data)) {
                    setWarehouses(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch warehouses for global filter:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchWarehouses();
    }, [isLoggedIn]);

    /**
     * Update selected warehouse IDs and persist to sessionStorage
     */
    const setWarehouseIds = useCallback((ids) => {
        const nextIds = Array.isArray(ids) ? ids : [];
        setSelectedWarehouseIds(nextIds);
        try {
            sessionStorage.setItem('selected_warehouse_ids', JSON.stringify(nextIds));
        } catch (e) {
            console.error('Failed to save selected_warehouse_ids to sessionStorage', e);
        }
    }, []);

    /**
     * Clear all selected warehouses
     */
    const clearSelection = useCallback(() => {
        setWarehouseIds([]);
    }, [setWarehouseIds]);

    const value = {
        warehouses,
        selectedWarehouseIds,
        setWarehouseIds,
        clearSelection,
        loading
    };

    return (
        <WarehouseContext.Provider value={value}>
            {children}
        </WarehouseContext.Provider>
    );
};

/**
 * Hook to use the warehouse filter context
 */
export const useWarehouseContext = () => {
    const context = useContext(WarehouseContext);
    if (context === undefined) {
        throw new Error('useWarehouseContext must be used within a WarehouseProvider');
    }
    return context;
};

export default WarehouseContext;
